import React, { useEffect, useState, useRef } from 'react';
import '../style/AdminBlogPreview.css';

/* ─── helpers ─────────────────────────────────────────────── */
const timeAgo = (timestamp) => {
  const diff   = Date.now() - new Date(timestamp);
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(mins  / 60);
  const days   = Math.floor(hours / 24);
  const weeks  = Math.floor(days  / 7);
  const months = Math.floor(days  / 30);
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
  if (weeks  > 0) return `${weeks} week${weeks  > 1 ? 's' : ''} ago`;
  if (days   > 0) return `${days} day${days    > 1 ? 's' : ''} ago`;
  if (hours  > 0) return `${hours} hour${hours  > 1 ? 's' : ''} ago`;
  if (mins   > 0) return `${mins} min${mins    > 1 ? 's' : ''} ago`;
  return 'just now';
};

const readingTime = (body = []) => {
  const words = body.reduce((acc, block) => {
    const text = block.data?.text || (block.data?.items || []).join(' ');
    return acc + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(1, Math.ceil(words / 200));
};

const objToHtml = ({ type, data }) => {
  if (!data) return '';
  if (type === 'header')
    return `<h${data.level} class="abp-h">${data.text}</h${data.level}>`;
  if (type === 'paragraph')
    return `<p class="abp-p">${data.text}</p>`;
  if (type === 'list') {
    const items = (data.items || []).map(i => `<li>${i}</li>`).join('');
    return data.style === 'ordered'
      ? `<ol class="abp-list">${items}</ol>`
      : `<ul class="abp-list">${items}</ul>`;
  }
  if (type === 'image')
    return `<figure class="abp-figure">
      <img src="${data.file?.url}" alt="${data.caption || ''}" loading="lazy" />
      ${data.caption ? `<figcaption>${data.caption}</figcaption>` : ''}
    </figure>`;
  if (type === 'quote')
    return `<blockquote class="abp-quote">${data.text}${data.caption ? `<cite>— ${data.caption}</cite>` : ''}</blockquote>`;
  if (type === 'code')
    return `<pre class="abp-code"><code>${data.code}</code></pre>`;
  return '';
};

/* ════════════════════════════════════════════════════════════
   ADMIN BLOG PREVIEW
════════════════════════════════════════════════════════════ */
const AdminBlogPreview = ({ blog, onClose, onApprove, onRevoke }) => {
  const [acting, setActing]   = useState(false);
  const [pct,    setPct]      = useState(0);
  const drawerRef             = useRef(null);
  const mins                  = readingTime(blog.body || []);
  const isApproved            = blog.isApproved;

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  /* reading progress inside drawer */
  useEffect(() => {
    const el = drawerRef.current;
    if (!el) return;
    const handler = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const total = scrollHeight - clientHeight;
      setPct(total > 0 ? Math.round((scrollTop / total) * 100) : 0);
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  const handleAction = async (action) => {
    setActing(true);
    try { await action(); } finally { setActing(false); }
  };

  return (
    <div className="abp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="abp-drawer" ref={drawerRef}>

        {/* Progress bar */}
        <div className="abp-progress" aria-hidden="true">
          <div className="abp-progress__fill" style={{ width: `${pct}%` }} />
        </div>

        {/* Sticky header */}
        <div className="abp-drawer__header">
          <div className="abp-drawer__header-left">
            <button className="abp-back" onClick={onClose} aria-label="Close preview">
              ← Back
            </button>
            <span className={`abp-status abp-status--${isApproved ? 'approved' : 'pending'}`}>
              {isApproved ? '✅ Approved' : '⏳ Pending'}
            </span>
          </div>

          <div className="abp-drawer__header-actions">
            {!isApproved ? (
              <button
                className="abp-action-btn abp-action-btn--approve"
                onClick={() => handleAction(() => onApprove(blog._id))}
                disabled={acting}
              >
                {acting ? (
                  <span className="abp-spinner" />
                ) : (
                  '✓ Approve Blog'
                )}
              </button>
            ) : (
              <button
                className="abp-action-btn abp-action-btn--revoke"
                onClick={() => handleAction(() => onRevoke(blog._id))}
                disabled={acting}
              >
                {acting ? <span className="abp-spinner" /> : '✕ Revoke Approval'}
              </button>
            )}
          </div>
        </div>

        {/* Blog content */}
        <div className="abp-content">
          {blog.thumbnail && blog.thumbnail !== '#' && (
            <div className="abp-cover">
              <img src={blog.thumbnail} alt={blog.title} className="abp-cover__img" />
            </div>
          )}

          <article className="abp-article">
            {/* Tags */}
            {blog.tags?.length > 0 && (
              <div className="abp-tags">
                {blog.tags.map(t => (
                  <span className="abp-tag" key={t}>#{t}</span>
                ))}
              </div>
            )}

            <h1 className="abp-title">{blog.title}</h1>

            {/* Author row */}
            <div className="abp-author">
              <img
                className="abp-author__avatar"
                src={blog.authorDetails?.image || '/default-avatar.png'}
                alt={blog.authorDetails?.username}
                loading="lazy"
              />
              <div className="abp-author__info">
                <span className="abp-author__name">{blog.authorDetails?.username || 'Unknown Author'}</span>
                <div className="abp-author__meta">
                  <span>{blog.authorDetails?.email}</span>
                  <span className="abp-dot">·</span>
                  <span>Published {timeAgo(blog.createdAt)}</span>
                  <span className="abp-dot">·</span>
                  <span>{mins} min read</span>
                </div>
              </div>
            </div>

            {/* Admin info bar */}
            <div className="abp-infobar">
              <div className="abp-infobar__item">
                <span className="abp-infobar__icon">👁️</span>
                <span>{blog.views || 0} views</span>
              </div>
              <div className="abp-infobar__item">
                <span className="abp-infobar__icon">👏</span>
                <span>{blog.likeCount || 0} claps</span>
              </div>
              <div className="abp-infobar__item">
                <span className="abp-infobar__icon">💬</span>
                <span>{blog.commentCount || 0} comments</span>
              </div>
              <div className="abp-infobar__item">
                <span className="abp-infobar__icon">📋</span>
                <span className={`abp-status-chip abp-status-chip--${blog.status}`}>{blog.status}</span>
              </div>
            </div>

            <hr className="abp-divider" />

            {/* Body */}
            <div
              className="abp-body"
              dangerouslySetInnerHTML={{ __html: (blog.body || []).map(objToHtml).join('') }}
            />

            <hr className="abp-divider" />

            {/* Footer action */}
            <div className="abp-footer-action">
              <p className="abp-footer-action__label">
                {isApproved
                  ? 'This blog is live and visible to all users.'
                  : 'Review the content above and approve if it meets your guidelines.'}
              </p>
              {!isApproved ? (
                <button
                  className="abp-action-btn abp-action-btn--approve abp-action-btn--lg"
                  onClick={() => handleAction(() => onApprove(blog._id))}
                  disabled={acting}
                >
                  {acting ? <span className="abp-spinner" /> : '✓ Approve This Blog'}
                </button>
              ) : (
                <button
                  className="abp-action-btn abp-action-btn--revoke abp-action-btn--lg"
                  onClick={() => handleAction(() => onRevoke(blog._id))}
                  disabled={acting}
                >
                  {acting ? <span className="abp-spinner" /> : '✕ Revoke Approval'}
                </button>
              )}
            </div>
          </article>
        </div>

      </div>
    </div>
  );
};

export default AdminBlogPreview;